import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Auth Module Service Stores
 * Services for authentication, user activation, and password management
 */
export const authStores: ServiceStoreConfig[] = [
  {
    name: "activate-user",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.activateUserStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalActivations = entities.length;
          const successfulActivations = entities.filter(
            (activation: Record<string, unknown>) =>
              activation.status === "activated" || activation.status === "success"
          ).length;
          const pendingActivations = entities.filter(
            (activation: Record<string, unknown>) =>
              activation.status === "pending"
          ).length;
          const failedActivations = entities.filter(
            (activation: Record<string, unknown>) =>
              activation.status === "failed"
          ).length;

          return [
            {
              title: "Total Activations",
              value: () => totalActivations.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Successful",
              value: () => successfulActivations.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pendingActivations.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Failed",
              value: () => failedActivations.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: false,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "auth-redirect",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.authRedirectStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalRedirects = entities.length;
          const recentRedirects = entities.filter(
            (redirect: Record<string, unknown>) => {
              const createdDate = new Date(redirect.createdAt as string);
              const hourAgo = new Date();
              hourAgo.setHours(hourAgo.getHours() - 1);
              return createdDate > hourAgo;
            }
          ).length;

          return [
            {
              title: "Total Redirects",
              value: () => totalRedirects.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Recent (1 hour)",
              value: () => recentRedirects.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: false,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "email-login",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.emailLoginStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalLogins = entities.length;
          const successfulLogins = entities.filter(
            (login: Record<string, unknown>) =>
              login.status === "success" || login.authenticated === true
          ).length;
          const failedLogins = entities.filter(
            (login: Record<string, unknown>) =>
              login.status === "failed" || login.authenticated === false
          ).length;
          const recentLogins = entities.filter(
            (login: Record<string, unknown>) => {
              const loginDate = new Date(login.createdAt as string);
              const dayAgo = new Date();
              dayAgo.setDate(dayAgo.getDate() - 1);
              return loginDate > dayAgo;
            }
          ).length;

          return [
            {
              title: "Total Logins",
              value: () => totalLogins.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Successful",
              value: () => successfulLogins.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failedLogins.toString(),
              valueColor: "error.main",
            },
            {
              title: "Recent (24h)",
              value: () => recentLogins.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: false,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "password-reset",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.passwordResetStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalResets = entities.length;
          const completedResets = entities.filter(
            (reset: Record<string, unknown>) =>
              reset.status === "completed" || reset.resetCompleted === true
          ).length;
          const pendingResets = entities.filter(
            (reset: Record<string, unknown>) =>
              reset.status === "pending" || reset.resetCompleted === false
          ).length;
          const expiredResets = entities.filter(
            (reset: Record<string, unknown>) =>
              reset.status === "expired"
          ).length;

          return [
            {
              title: "Total Resets",
              value: () => totalResets.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Completed",
              value: () => completedResets.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pendingResets.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Expired",
              value: () => expiredResets.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: false,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "refresh-token",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.refreshTokenStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalTokens = entities.length;
          const activeTokens = entities.filter(
            (token: Record<string, unknown>) =>
              token.status === "active" || !token.revoked
          ).length;
          const revokedTokens = entities.filter(
            (token: Record<string, unknown>) =>
              token.status === "revoked" || token.revoked === true
          ).length;
          const expiringTokens = entities.filter(
            (token: Record<string, unknown>) => {
              const expiryDate = new Date(token.expiresAt as string);
              const dayFromNow = new Date();
              dayFromNow.setDate(dayFromNow.getDate() + 1);
              return expiryDate < dayFromNow && !token.revoked;
            }
          ).length;

          return [
            {
              title: "Total Tokens",
              value: () => totalTokens.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeTokens.toString(),
              valueColor: "success.main",
            },
            {
              title: "Revoked",
              value: () => revokedTokens.toString(),
              valueColor: "error.main",
            },
            {
              title: "Expiring Soon",
              value: () => expiringTokens.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
];